import { FC } from 'react';

import { cn } from '@/utils/tailwind';

import { CurveLineIcon, CurveLineSmallIcon } from '../../../assets/icons';
import { LINE_CHART_SIMPLE_TEST_IDS } from '../../../constants/data-testids';
import { Card, CardContent } from '../../atoms/Card/Card';
import { Skeleton } from '../../atoms/Skeleton/Skeleton';

type LineChartSimpleSkeletonProps = {
  size: '1/3' | '2/3' | '3/3';
};

export const LineChartSimpleSkeleton: FC<LineChartSimpleSkeletonProps> = ({ size }) => {
  const renderSkeletonBySize = () => {
    switch (size) {
      case '1/3': {
        return (
          <div className="flex flex-col gap-4 mb-6">
            <div className="flex gap-2">
              <Skeleton className="inline w-3 h-3 rounded-full" />
              <Skeleton className="w-[180px] h-[14px]" />
            </div>
            <div className="flex flex-col">
              <div className="flex items-center gap-2 min-h-[18px]">
                <Skeleton className="w-[60px] h-[14px]" />
                <Skeleton className="w-[30px] h-[14px]" />
              </div>
              <div className="flex items-center gap-2 min-h-[18px]">
                <Skeleton className="w-[30px] h-[14px]" />
                <Skeleton className="w-[200px] h-[14px]" />
              </div>
            </div>
          </div>
        );
      }
      case '2/3': {
        return (
          <div className="flex justify-between items-start mb-4">
            <div className="flex flex-col">
              <div className="flex items-center gap-2 min-h-[18px]">
                <Skeleton className="w-[60px] h-[14px]" />
                <Skeleton className="w-[30px] h-[14px]" />
              </div>
              <div className="flex items-center gap-2 min-h-[18px]">
                <Skeleton className="w-[30px] h-[14px]" />
                <Skeleton className="w-[200px] h-[14px]" />
              </div>
            </div>
            <div className="flex h-fit justify-start flex-wrap gap-3">
              {Array.from({ length: 2 }, (_, i) => (
                <div className="flex items-center gap-2" key={i}>
                  <Skeleton className="w-3 h-3 rounded-full" />
                  <Skeleton className="w-[107px] h-[14px] rounded-sm" />
                </div>
              ))}
            </div>
          </div>
        );
      }
      case '3/3': {
        return (
          <div className="flex justify-between items-start mb-6">
            <div>
              <Skeleton className="w-[140px] h-[14px]" />
            </div>
            <div className="flex h-fit justify-start flex-wrap gap-3">
              {Array.from({ length: 2 }, (_, i) => (
                <div className="flex items-center gap-2" key={i}>
                  <Skeleton className="w-3 h-3 rounded-full" />
                  <Skeleton className="w-[107px] h-[14px] rounded-sm" />
                </div>
              ))}
            </div>
          </div>
        );
      }
    }
  };
  return (
    <Card
      data-testid={LINE_CHART_SIMPLE_TEST_IDS.SKELETON}
      className="p-4 border-custom-warm-grey-150"
    >
      <CardContent>
        {renderSkeletonBySize()}
        <div className="flex gap-3 mb-[18px] pr-[39px]">
          <div className="flex flex-col gap-[47.5px]">
            {Array.from({ length: 5 }, (_, i) => (
              <Skeleton key={i} className="w-[30px] h-[14px] rounded-[4px]" />
            ))}
          </div>
          <div className="relative w-full overflow-hidden flex flex-col">
            <CurveLineIcon className="hidden sm:inline relative h-full z-10 animate-pulse" />
            <CurveLineIcon className="hidden sm:inline relative h-full z-10 animate-pulse rotate-180" />

            <CurveLineSmallIcon className="inline sm:hidden relative h-full z-10 animate-pulse" />
            <CurveLineSmallIcon className="inline sm:hidden relative h-full z-10 animate-pulse rotate-180" />

            <span
              className="absolute top-[5px] w-full h-[1px] animate-pulse"
              style={{
                backgroundImage:
                  'linear-gradient(90deg, #F0EEED 33.33%, transparent 33.33%, transparent 66.66%, #F0EEED 66.66%)',
                backgroundSize: '24px 100%',
              }}
            />

            <span
              className="absolute top-[68px] w-full h-[1px] animate-pulse"
              style={{
                backgroundImage:
                  'linear-gradient(90deg, #F0EEED 33.33%, transparent 33.33%, transparent 66.66%, #F0EEED 66.66%)',
                backgroundSize: '24px 100%',
              }}
            />

            <span
              className="absolute top-[129px] w-full h-[1px] animate-pulse"
              style={{
                backgroundImage:
                  'linear-gradient(90deg, #F0EEED 33.33%, transparent 33.33%, transparent 66.66%, #F0EEED 66.66%)',
                backgroundSize: '24px 100%',
              }}
            />

            <span
              className="absolute top-[192px] w-full h-[1px] animate-pulse"
              style={{
                backgroundImage:
                  'linear-gradient(90deg, #F0EEED 33.33%, transparent 33.33%, transparent 66.66%, #F0EEED 66.66%)',
                backgroundSize: '24px 100%',
              }}
            />

            <span
              className="absolute top-[252px] w-full h-[1px] animate-pulse"
              style={{
                backgroundImage:
                  'linear-gradient(90deg, #F0EEED 33.33%, transparent 33.33%, transparent 66.66%, #F0EEED 66.66%)',
                backgroundSize: '24px 100%',
              }}
            />
          </div>
        </div>
        <div className="flex items-center justify-between mr-[39px] ml-[42px]">
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} className={cn('w-[24px] sm:w-[40px] h-[14px] rounded-[4px]')} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
